// frontend/src/components/common/PlanLimitBar.tsx
// Planın günlük ders / AI mesaj / proje kullanımını progress bar ile gösterir.
// Limit dolunca kırmızı, %80+ ise sarı, normal ise yeşil/mavi.

import { Link } from 'react-router-dom';
import { usePlanUsage } from '../../hooks/usePlanUsage';

interface PlanLimitBarProps {
  /** Sadece belirli tipleri göster. Default: hepsini göster */
  show?: ('lesson' | 'ai_message' | 'project')[];
  compact?: boolean;
}

const BAR_CONFIG = {
  lesson: {
    label: 'Günlük Ders',
    icon: '📚',
    getUsed: (u: any) => u.lessonsUsed,
    getLimit: (u: any) => u.lessonsLimit,
  },
  ai_message: {
    label: 'AI Mesaj',
    icon: '🤖',
    getUsed: (u: any) => u.aiMessagesUsed,
    getLimit: (u: any) => u.aiMessagesLimit,
  },
  project: {
    label: 'Proje',
    icon: '🚀',
    getUsed: (u: any) => u.projectsUsed,
    getLimit: (u: any) => u.projectsLimit,
  },
} as const;

type BarType = keyof typeof BAR_CONFIG;

export function PlanLimitBar({ show, compact = false }: PlanLimitBarProps) {
  const { usage, isLoading } = usePlanUsage();

  if (isLoading || !usage) return null;

  const types: BarType[] = show ?? ['lesson', 'ai_message', 'project'];
  // Sadece limiti olan tipleri göster (null = unlimited → gizle)
  const visibleTypes = types.filter(t => BAR_CONFIG[t].getLimit(usage) !== null);

  if (visibleTypes.length === 0) return null; // unlimited plan → hiçbir bar gösterme

  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: compact ? 10 : 14,
        padding: compact ? '10px 14px' : '14px 18px',
        display: 'flex',
        flexDirection: compact ? 'row' : 'column',
        gap: compact ? 16 : 10,
        alignItems: compact ? 'center' : undefined,
        flexWrap: 'wrap',
      }}
    >
      {/* Plan adı */}
      {!compact && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 4 }}>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            {usage.planName} Planı · Günlük Kullanım
          </span>
          <Link
            to="/pricing"
            style={{ fontSize: 11, color: 'var(--primary)', fontWeight: 700, textDecoration: 'none' }}
          >
            Planı Yükselt →
          </Link>
        </div>
      )}

      {visibleTypes.map(type => {
        const cfg = BAR_CONFIG[type];
        const used = cfg.getUsed(usage);
        const limit = cfg.getLimit(usage);
        const pct = Math.min(100, Math.round((used / limit) * 100));
        const atLimit = used >= limit;
        const nearLimit = !atLimit && pct >= 80;

        const barColor = atLimit ? '#ef4444' : nearLimit ? '#f59e0b' : '#10b981';
        const textColor = atLimit ? '#ef4444' : nearLimit ? '#f59e0b' : 'var(--text-secondary)';

        return (
          <div key={type} style={{ flex: 1, minWidth: compact ? 100 : undefined }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
              <span style={{ fontSize: compact ? 11 : 12, color: textColor, fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }}>
                {cfg.icon} {compact ? '' : cfg.label}
              </span>
              <span style={{ fontSize: 11, color: textColor, fontWeight: 700 }}>
                {used}/{limit}
                {atLimit && ' 🔒'}
              </span>
            </div>
            <div style={{ height: compact ? 4 : 6, background: 'var(--border)', borderRadius: 99, overflow: 'hidden' }}>
              <div
                style={{
                  height: '100%',
                  width: `${pct}%`,
                  background: barColor,
                  borderRadius: 99,
                  transition: 'width 0.4s ease',
                  boxShadow: atLimit ? `0 0 6px ${barColor}80` : undefined,
                }}
              />
            </div>
          </div>
        );
      })}

      {/* "Yarın sıfırlanır" notu */}
      {!compact && (
        <p style={{ fontSize: 11, color: 'var(--text-muted)', margin: '4px 0 0 0' }}>
          🔄 Limitler her gün gece yarısı (UTC) otomatik sıfırlanır.
        </p>
      )}
    </div>
  );
}

/** Belirli bir tip için limit dolmuş mu? — hook yerine bağımsız kullanım için */
export function PlanLimitGate({
  type,
  children,
  fallback,
}: {
  type: 'lesson' | 'ai_message' | 'project';
  children: React.ReactNode;
  fallback?: React.ReactNode;
}) {
  const { usage, isLoading } = usePlanUsage();
  if (isLoading || !usage) return <>{children}</>;

  const cfg = BAR_CONFIG[type];
  const used = cfg.getUsed(usage);
  const limit = cfg.getLimit(usage);
  if (limit !== null && used >= limit) {
    return fallback ? <>{fallback}</> : null;
  }
  return <>{children}</>;
}
